import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { ContactForm } from '../components/contact/ContactForm';
import { directContacts, InquiryType, inquiryReasons } from '../data/contact';
import { easeOutStrong, fadeUp } from '../utils/motion';
export function ContactPage() {
  const [presetInquiry, setPresetInquiry] = useState<InquiryType>('');
  const chooseReason = (value: InquiryType) => {
    setPresetInquiry(value);
    document.getElementById('contact-form')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
    window.setTimeout(() => document.getElementById('firstName')?.focus({
      preventScroll: true
    }), 400);
  };
  return <>
      <section className="bg-surface-bg border-b border-line pt-32 pb-20 lg:pt-44 lg:pb-28" data-id="element-1707" data-mp="ez4ljgup77b8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16" data-id="element-1708" data-mp="1lfgcoxz3roxb">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: easeOutStrong
        }} className="lg:col-span-5" data-id="element-1709" data-mp="1qcyoqx8f2k9j">
            <p className="text-sm font-medium text-teal mb-5" data-id="element-1710" data-mp="mzla4mq7yf87">Contact</p>
            <h1 className="font-heading font-extrabold text-5xl lg:text-6xl text-ink-dark leading-[1.05] tracking-tight mb-8" data-id="element-1711" data-mp="hyxhxxq97sce">
              Get in Touch
            </h1>
            <p className="text-xl text-ink-dark leading-relaxed mb-12" data-id="element-1712" data-mp="81kgy0gq6hhr">
              We’re interested in hearing from organizations, institutions,
              researchers, and leaders who share our commitment to strengthening
              human capability, improving judgment quality, and building more
              resilient systems.
            </p>

            <dl className="border-t border-line" data-id="element-1713" data-mp="3qhmx6349bwiy">
              {directContacts.map(contact => <div key={contact.email} className="border-b border-line py-5" data-id="element-1714" data-mp="3lfxh2926gso0">
                  <dt className="text-sm text-ink-muted mb-1" data-id="element-1715" data-mp="3gixg2ck6qg3r">{contact.label}</dt>
                  <dd data-id="element-1716" data-mp="36syuosi6gog5">
                    <a href={`mailto:${contact.email}`} className="font-heading font-bold text-lg text-navy hover:text-navy-light transition-colors duration-150" data-id="element-1717" data-mp="36sfdje0cq436">
                      {contact.email}
                    </a>
                  </dd>
                </div>)}
              <div className="py-5" data-id="element-1718" data-mp="3pkrq0tkpylr1">
                <dt className="text-sm text-ink-muted mb-1" data-id="element-1719" data-mp="3plsxko2j0q40">Response time</dt>
                <dd className="text-ink-dark leading-relaxed" data-id="element-1720" data-mp="3ujujdgrc73j6">
                  We typically respond to inquiries within 2–3 business days.
                  For urgent matters, please indicate that in your message.
                </dd>
              </div>
            </dl>
          </motion.div>

          <motion.div id="contact-form" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          delay: 0.06,
          ease: easeOutStrong
        }} className="lg:col-span-7 scroll-mt-28" data-id="element-1721" data-mp="cdpwl8pun6s2">
            <ContactForm presetInquiry={presetInquiry} data-id="element-1722" data-mp="1wwa64q0ax5zz" />
          </motion.div>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-28" data-id="element-1723" data-mp="2blg3e8fkwm58">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1724" data-mp="2bk4z87i81cdl">
          <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-12" data-id="element-1725" data-mp="26mv1m4kohueo">
            <h2 className="lg:col-span-5 font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight leading-tight" data-id="element-1726" data-mp="1ixah3h8vmp3a">
              Reasons to reach out
            </h2>
            <p className="lg:col-span-7 text-xl text-ink-muted leading-relaxed lg:pt-2" data-id="element-1727" data-mp="1suoypk6jdjx6">
              Whether you’re exploring a partnership, requesting a demo, or have
              questions about our research, we’d like to hear from you. Choose a
              topic to start your message.
            </p>
          </motion.div>

          <motion.ul {...fadeUp} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 border-t border-line" data-id="element-1728" data-mp="2z9zaikmw6svq">
            {inquiryReasons.map(reason => <li key={reason.value} className="border-b border-line" data-id="element-1729" data-mp="1drowfx79m56t">
                <button type="button" onClick={() => chooseReason(reason.value)} className="group flex w-full h-full items-start gap-4 py-7 text-left" data-id="element-1730" data-mp="1hubq83nhy06n">
                  <span className="flex-1" data-id="element-1731" data-mp="1rt183wfe7kmw">
                    <span className="block font-heading font-bold text-lg text-ink-dark group-hover:text-navy transition-colors duration-150" data-id="element-1732" data-mp="1rq5c4dn3v613">
                      {reason.title}
                    </span>
                    <span className="mt-1.5 block text-ink-muted leading-relaxed" data-id="element-1733" data-mp="21k5lwstc0zg5">{reason.description}</span>
                  </span>
                  <ArrowRight size={18} aria-hidden="true" className="mt-1 shrink-0 text-teal transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-1734" data-mp="2aj9845nspuyv" />
                </button>
              </li>)}
          </motion.ul>
        </div>
      </section>
    </>;
}